"use client";

import { motion } from "motion/react";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  HiBeaker,
  HiCalendarDays,
  HiChevronLeft,
  HiChevronRight,
  HiClock,
  HiExclamationTriangle,
  HiLightBulb,
  HiSparkles,
  HiStar,
} from "react-icons/hi2";

import Link from "next/link";
import { useParams } from "next/navigation";

import { formatCurrency } from "@/lib/format-data";
import { formatDisplayPrice } from "@/lib/service-fees";
import { cx } from "@/lib/tailwind-utils";

import type { SkinAnalysisAPIResponse } from "@/types/skin-analysis";

interface ResultsSectionProps {
  result: SkinAnalysisAPIResponse;
  age?: number;
}

const STEP_LABELS = [
  "See Zone Breakdown",
  "View Concerns",
  "Get Recommendations",
];

const staggerContainer = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.1 },
  },
};

const staggerItem = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

function useCountUp(target: number, duration = 1500) {
  const [value, setValue] = useState(0);
  const startTime = useRef<number | null>(null);

  useEffect(() => {
    const animate = (timestamp: number) => {
      if (!startTime.current) startTime.current = timestamp;
      const elapsed = timestamp - startTime.current;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(target * eased));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);

  return value;
}

function ScoreRing({ score, size = 130 }: { score: number; size?: number }) {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const animatedScore = useCountUp(score, 1800);
  const offset = circumference - (animatedScore / 100) * circumference;

  const gradientId = `score-gradient-${size}`;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="-rotate-90" width={size} height={size}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#0178D4" />
            <stop offset="100%" stopColor="#38B2FF" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-background-tertiary"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-display text-foreground">{animatedScore}</span>
        <span className="text-caption-strong text-foreground-secondary">
          / 100
        </span>
      </div>
    </div>
  );
}

function ScoreBar({
  score,
  label,
  visited,
}: {
  score: number;
  label: string;
  visited: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-body">
        <span className="font-medium text-foreground-secondary">{label}</span>
        <span className="font-semibold text-foreground">{score}</span>
      </div>
      <div className="h-2.5 w-full overflow-hidden rounded-full bg-background-tertiary">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-primary to-primary-400"
          initial={{ width: "0%" }}
          animate={{ width: visited ? `${score}%` : "0%" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: string }) {
  const classes =
    {
      Mild: "bg-status-warning-bg text-status-warning-text",
      Moderate: "bg-status-orange-bg text-status-orange-text",
      Significant: "bg-status-error-bg text-status-error-text",
    }[severity] ?? "bg-background-secondary text-foreground-secondary";

  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-caption-emphasis",
        classes,
      )}
    >
      {severity}
    </span>
  );
}

function CategoryIcon({
  category,
  isClinicService,
}: {
  category: string;
  isClinicService?: boolean;
}) {
  const iconClass = "h-5 w-5";
  if (isClinicService) {
    return (
      <HiCalendarDays className={cx(iconClass, "text-status-purple-text")} />
    );
  }
  switch (category) {
    case "Professional Treatment":
      return <HiBeaker className={cx(iconClass, "text-status-purple-text")} />;
    case "Skincare Routine":
      return (
        <HiSparkles className={cx(iconClass, "text-status-emerald-text")} />
      );
    case "Lifestyle":
      return (
        <HiLightBulb className={cx(iconClass, "text-accent-foreground")} />
      );
    default:
      return <HiStar className={cx(iconClass, "text-foreground-secondary")} />;
  }
}

function PriorityDot({ priority }: { priority: string }) {
  const color =
    {
      High: "bg-status-error-text",
      Medium: "bg-status-warning-text",
      Low: "bg-accent-foreground",
    }[priority] ?? "bg-foreground-secondary";

  return <div className={cx("h-2 w-2 rounded-full", color)} />;
}

// ─── Carousel constants ───
const PEEK = 32; // px of adjacent card visible on each side
const GAP = 16; // px between cards

export default function ResultsSection({ result, age }: ResultsSectionProps) {
  const skinAge = useCountUp(result.overallSkinAge, 1500);
  const params = useParams();
  const tenant = params.tenant as string;

  const hasConcerns = result.concerns.length > 0;
  const hasRecommendations = result.recommendations.length > 0;

  const steps: string[] = ["hero", "zones"];
  if (hasConcerns) steps.push("concerns");
  if (hasRecommendations) steps.push("recommendations");
  const totalSteps = steps.length;

  const [step, setStep] = useState(0);
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(
    () => new Set([0]),
  );

  // Measure container width for card sizing
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [wrapperWidth, setWrapperWidth] = useState(0);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const measure = () => setWrapperWidth(el.offsetWidth);
    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Measure active card height for smooth container sizing
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeHeight, setActiveHeight] = useState<number | undefined>();

  const measureHeight = useCallback(() => {
    const card = cardRefs.current[step];
    if (card) setActiveHeight(card.offsetHeight);
  }, [step]);

  useEffect(() => {
    measureHeight();
    window.addEventListener("resize", measureHeight);
    return () => window.removeEventListener("resize", measureHeight);
  }, [measureHeight]);

  useEffect(() => {
    const timer = setTimeout(measureHeight, 500);
    return () => clearTimeout(timer);
  }, [step, measureHeight]);

  const goTo = (target: number) => {
    if (target === step || target < 0 || target >= totalSteps) return;
    setStep(target);
    setVisitedSteps((prev) => {
      const next = new Set(prev);
      next.add(target);
      return next;
    });
  };

  const cardWidth = wrapperWidth > 0 ? wrapperWidth - PEEK * 2 : 0;
  const slideOffset = -step * (cardWidth + GAP);

  const nextLabel = (): string => {
    const nextStep = steps[step + 1];
    if (nextStep === "zones") return STEP_LABELS[0];
    if (nextStep === "concerns") return STEP_LABELS[1];
    if (nextStep === "recommendations") return STEP_LABELS[2];
    return "Next";
  };

  return (
    <div className="space-y-5">
      {/* Step indicator dots */}
      <div className="flex items-center justify-center gap-2">
        {steps.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={cx(
              "h-2.5 w-2.5 cursor-pointer rounded-full transition-all duration-300",
              i === step
                ? "scale-110 bg-primary"
                : visitedSteps.has(i)
                  ? "bg-primary/30"
                  : "bg-background-tertiary",
            )}
            aria-label={`Go to step ${i + 1}`}
          />
        ))}
      </div>

      {/* Carousel */}
      <div ref={wrapperRef} className="relative">
        {/* Left arrow */}
        <button
          onClick={() => goTo(step - 1)}
          className={cx(
            "absolute -left-1 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-background shadow-md transition-all",
            step > 0
              ? "opacity-100 hover:shadow-lg"
              : "pointer-events-none opacity-0",
          )}
          aria-label="Previous"
        >
          <HiChevronLeft className="h-4 w-4 text-foreground-secondary" />
        </button>

        {/* Right arrow */}
        <button
          onClick={() => goTo(step + 1)}
          className={cx(
            "absolute -right-1 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-background shadow-md transition-all",
            step < totalSteps - 1
              ? "opacity-100 hover:shadow-lg"
              : "pointer-events-none opacity-0",
          )}
          aria-label="Next"
        >
          <HiChevronRight className="h-4 w-4 text-foreground-secondary" />
        </button>

        {/* Sliding track */}
        <motion.div
          className="overflow-hidden"
          animate={{ height: (activeHeight ?? 400) + 4 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <motion.div
            className="flex items-start"
            style={{
              gap: GAP,
              paddingLeft: PEEK,
              paddingRight: PEEK,
            }}
            animate={{ x: slideOffset }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={(_, info) => {
              const threshold = 50;
              const vThreshold = 300;
              if (info.offset.x < -threshold || info.velocity.x < -vThreshold) {
                if (step < totalSteps - 1) goTo(step + 1);
              } else if (
                info.offset.x > threshold ||
                info.velocity.x > vThreshold
              ) {
                if (step > 0) goTo(step - 1);
              }
            }}
          >
            {steps.map((stepName, i) => {
              const isActive = i === step;

              return (
                <div
                  key={stepName}
                  ref={(el) => {
                    cardRefs.current[i] = el;
                  }}
                  style={{
                    width: cardWidth || "100%",
                    flexShrink: 0,
                  }}
                  className={cx(
                    "transition-all duration-300",
                    isActive
                      ? "scale-100 opacity-100"
                      : "cursor-pointer scale-[0.97] opacity-50",
                  )}
                  onClick={!isActive ? () => goTo(i) : undefined}
                >
                  {/* ---- Hero Card ---- */}
                  {stepName === "hero" && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.96, y: 12 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: "easeOut" }}
                      className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-primary-50/50 p-8 text-center shadow-sm"
                    >
                      <motion.div
                        className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-primary/5"
                        animate={{ x: [0, 10, 0], y: [0, -8, 0] }}
                        transition={{
                          duration: 12,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />
                      <motion.div
                        className="pointer-events-none absolute -bottom-8 -left-8 h-32 w-32 rounded-full bg-primary-100/30"
                        animate={{ x: [0, -8, 0], y: [0, 10, 0] }}
                        transition={{
                          duration: 15,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                      />

                      <div className="relative">
                        <p className="mb-1 text-caption-emphasis uppercase tracking-widest text-primary">
                          Your True Skin Age
                        </p>
                        <p className="text-7xl font-bold tracking-tight text-foreground">
                          {skinAge}
                        </p>

                        {result.ageDifference !== null && age && (
                          <div
                            className={cx(
                              "mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-body-emphasis",
                              result.ageDifference > 0
                                ? "bg-status-success-bg text-status-success-text"
                                : result.ageDifference < 0
                                  ? "bg-status-error-bg text-status-error-text"
                                  : "bg-background-secondary text-foreground-secondary",
                            )}
                          >
                            {result.ageDifference > 0
                              ? `${result.ageDifference} years younger than your age`
                              : result.ageDifference < 0
                                ? `${Math.abs(result.ageDifference)} years older than your age`
                                : "Right on track with your age"}
                          </div>
                        )}

                        <div className="mt-6 flex justify-center">
                          <ScoreRing score={result.overallScore} />
                        </div>
                        <p className="mx-auto mt-5 max-w-md text-body leading-relaxed text-foreground-secondary">
                          {result.summary}
                        </p>
                      </div>
                    </motion.div>
                  )}

                  {/* ---- Zone Breakdown ---- */}
                  {stepName === "zones" && (
                    <div className="rounded-2xl border border-border bg-background p-6 shadow-sm">
                      <h3 className="mb-5 text-h3 text-foreground">
                        Zone Breakdown
                      </h3>
                      <motion.div
                        className="space-y-5"
                        variants={staggerContainer}
                        initial="initial"
                        animate={visitedSteps.has(i) ? "animate" : "initial"}
                      >
                        {result.zones.map((zone) => (
                          <motion.div key={zone.zone} variants={staggerItem}>
                            <div className="mb-1.5 flex items-baseline justify-between">
                              <span className="text-body-emphasis text-foreground">
                                {zone.zone}
                              </span>
                              <span className="text-caption-strong text-foreground-muted">
                                Skin age: {zone.estimatedAge}
                              </span>
                            </div>
                            <ScoreBar
                              score={zone.score}
                              label={zone.primaryConcern}
                              visited={visitedSteps.has(i)}
                            />
                            <p className="mt-1.5 text-caption leading-relaxed text-foreground-muted">
                              {zone.detail}
                            </p>
                          </motion.div>
                        ))}
                      </motion.div>
                    </div>
                  )}

                  {/* ---- Concerns ---- */}
                  {stepName === "concerns" && (
                    <div className="rounded-2xl border border-border bg-background p-6 shadow-sm">
                      <div className="mb-4 flex items-center gap-2">
                        <HiExclamationTriangle className="h-5 w-5 text-status-warning-text" />
                        <h3 className="text-h3 text-foreground">
                          Key Concerns
                        </h3>
                      </div>
                      <motion.div
                        className="space-y-3"
                        variants={staggerContainer}
                        initial="initial"
                        animate={visitedSteps.has(i) ? "animate" : "initial"}
                      >
                        {result.concerns.map((concern) => (
                          <motion.div
                            key={concern.concern}
                            variants={staggerItem}
                            whileHover={isActive ? { y: -2 } : undefined}
                            transition={{
                              type: "spring",
                              stiffness: 300,
                              damping: 20,
                            }}
                            className="rounded-xl border border-border bg-background-secondary/50 p-4"
                          >
                            <div className="mb-1.5 flex items-center gap-2">
                              <span className="text-body-emphasis text-foreground">
                                {concern.concern}
                              </span>
                              <SeverityBadge severity={concern.severity} />
                            </div>
                            <p className="text-body leading-relaxed text-foreground-secondary">
                              {concern.description}
                            </p>
                          </motion.div>
                        ))}
                      </motion.div>
                    </div>
                  )}

                  {/* ---- Recommendations ---- */}
                  {stepName === "recommendations" && (
                    <div className="rounded-2xl border border-border bg-background p-6 shadow-sm">
                      <div className="mb-4 flex items-center gap-2">
                        <HiSparkles className="h-5 w-5 text-primary" />
                        <h3 className="text-h3 text-foreground">
                          Recommended For You
                        </h3>
                      </div>
                      <motion.div
                        className="space-y-3"
                        variants={staggerContainer}
                        initial="initial"
                        animate={visitedSteps.has(i) ? "animate" : "initial"}
                      >
                        {result.recommendations.map((rec) => {
                          const isClinicService = !!rec.serviceId;
                          const priceStr = isClinicService
                            ? formatDisplayPrice(
                                {
                                  displayPrice: rec.displayPrice ?? undefined,
                                  displayPriceMax:
                                    rec.displayPriceMax ?? undefined,
                                  priceStartsAt: rec.priceStartsAt,
                                },
                                formatCurrency,
                              )
                            : null;

                          return (
                            <motion.div
                              key={rec.title}
                              variants={staggerItem}
                              whileHover={isActive ? { y: -2 } : undefined}
                              transition={{
                                type: "spring",
                                stiffness: 300,
                                damping: 20,
                              }}
                              className="rounded-xl border border-border bg-background-secondary/50 p-4"
                            >
                              <div className="mb-2 flex items-start gap-3">
                                <div className="mt-0.5 flex-shrink-0">
                                  <CategoryIcon
                                    category={rec.category}
                                    isClinicService={isClinicService}
                                  />
                                </div>
                                <div className="flex-1">
                                  <div className="mb-0.5 flex items-center gap-2">
                                    <span className="text-body-emphasis text-foreground">
                                      {rec.title}
                                    </span>
                                    <PriorityDot priority={rec.priority} />
                                  </div>
                                  <p className="text-body leading-relaxed text-foreground-secondary">
                                    {rec.description}
                                  </p>
                                  <div className="mt-2 flex flex-wrap items-center gap-3">
                                    {priceStr && (
                                      <span className="text-body-emphasis text-foreground">
                                        {priceStr}
                                      </span>
                                    )}
                                    {isClinicService && rec.duration && (
                                      <span className="inline-flex items-center gap-1 text-caption text-foreground-muted">
                                        <HiClock className="h-3.5 w-3.5" />
                                        {rec.duration} min
                                      </span>
                                    )}
                                    {isClinicService && (
                                      <Link
                                        href={`/${tenant}/self-schedule`}
                                        className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-caption-emphasis text-white hover:bg-primary/90"
                                      >
                                        Book Now
                                      </Link>
                                    )}
                                    {!isClinicService && (
                                      <span className="text-caption-strong text-foreground-muted">
                                        {rec.category}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </motion.div>
                          );
                        })}
                      </motion.div>
                    </div>
                  )}
                </div>
              );
            })}
          </motion.div>
        </motion.div>
      </div>

      {/* Navigation buttons */}
      <div className="flex items-center justify-between pt-2">
        {step > 0 ? (
          <button
            onClick={() => goTo(step - 1)}
            className="text-body-strong text-foreground-secondary transition-colors hover:text-foreground"
          >
            &larr; Back
          </button>
        ) : (
          <div />
        )}
        {step < totalSteps - 1 && (
          <button
            onClick={() => goTo(step + 1)}
            className="rounded-full bg-gradient-to-r from-primary to-primary-400 px-5 py-2 text-body-emphasis text-white shadow-md shadow-primary/25 transition-all hover:shadow-lg hover:shadow-primary/30"
          >
            {nextLabel()} &rarr;
          </button>
        )}
      </div>
    </div>
  );
}
